import React from "react";
import { Star, MapPin, Navigation, Crown, ShieldCheck, BadgeCheck } from "lucide-react";
import { getRestaurantImage } from "@/lib/images";
import SmartImage from "@/components/ui/SmartImage";

function formatDistance(m) {
  if (m == null) return "";
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

const RestaurantCard = React.forwardRef(function RestaurantCard(
  { restaurant, active, onClick, onClaim, onReport, index = 0 },
  ref
) {
  const r = restaurant;
  const photo = getRestaurantImage(r);
  const directionsUrl =
    r.google_maps_uri ||
    `https://www.google.com/maps/dir/?api=1&destination=${r.lat},${r.lng}&destination_place_id=${r.place_id}`;

  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      data-testid={`restaurant-card-${r.place_id}`}
      onClick={onClick}
      onKeyDown={handleKey}
      aria-pressed={active}
      className={`group relative w-full text-left bg-white rounded-2xl overflow-hidden border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#D95D39] focus:ring-offset-2
        ${active ? "border-[#D95D39] shadow-lg ring-2 ring-[#D95D39]/20" : "border-[#E5E1D8] shadow-sm"}`}
      style={{ animationDelay: `${index * 40}ms` }}
    >
      <div className="relative h-40 w-full bg-[#F2EDE3] overflow-hidden">
        <SmartImage
          src={photo}
          alt={r.name}
          // First six cards above the mobile fold get an eager fetch with
          // high priority — by the time the user starts scrolling the
          // photos are already in. The rest pre-fetch via the SmartImage
          // IntersectionObserver (600px margin), so they're cached before
          // they enter the viewport.
          priority={index < 6}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent pointer-events-none" />

        {/* Distance pill */}
        {r.distance_m != null && (
          <span
            data-testid={`restaurant-distance-${r.place_id}`}
            className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium bg-white/90 backdrop-blur-sm text-[#2A3B2C] border border-white/60"
          >
            {formatDistance(r.distance_m)}
          </span>
        )}

        {/* Open/closed */}
        {r.business_status === "CLOSED_TEMPORARILY" ? (
          <span
            data-testid={`temporarily-closed-${r.place_id}`}
            className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FFF3EE] text-[#9C3A1C] border border-[#D95D39]/40"
            title="This place is temporarily closed — it may reopen"
          >
            Temp. closed
          </span>
        ) : r.open_now != null ? (
          <span
            className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-semibold ${
              r.open_now
                ? "bg-[#2A3B2C] text-white"
                : "bg-white/95 text-[#647466] border border-[#E5E1D8]"
            }`}
          >
            {r.open_now ? "Open" : "Closed"}
          </span>
        ) : null}

        {/* Featured badge */}
        {r.featured && (
          <span
            data-testid={`featured-badge-${r.place_id}`}
            className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#E1A624] to-[#D95D39] text-white shadow-md"
          >
            <Crown className="w-3 h-3" />
            Featured
          </span>
        )}

        {/* Halal verified badge */}
        {(r.badge_verified || r.halal_verified) && (
          <span
            data-testid={r.badge_verified ? `badge-verified-${r.place_id}` : `halal-badge-${r.place_id}`}
            className={`absolute bottom-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-md ${
              r.badge_verified
                ? "bg-[#2E7D5B] text-white"
                : "bg-[#2A3B2C] text-white"
            }`}
            title={
              r.badge_verified
                ? "Halal Verified · owner-paid trust badge"
                : r.halal_status === "certified"
                ? `Halal certified${r.halal_certification_body ? ` · ${r.halal_certification_body}` : ""}`
                : r.halal_status === "muslim_owned"
                ? "Muslim-owned · fully halal"
                : "Halal verified by owner"
            }
          >
            {r.badge_verified ? (
              <BadgeCheck className="w-3 h-3" />
            ) : (
              <ShieldCheck className="w-3 h-3" />
            )}
            {r.badge_verified
              ? "Halal Verified"
              : r.halal_status === "certified"
              ? "Certified"
              : "Halal Verified"}
          </span>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-lg font-medium text-[#2A3B2C] leading-tight line-clamp-1">
            {r.name}
          </h3>
          {r.rating != null && (
            <div className="flex items-center gap-1 shrink-0 text-[#2A3B2C]">
              <Star className="w-4 h-4 fill-[#E1A624] text-[#E1A624]" />
              <span className="text-sm font-semibold">{r.rating.toFixed(1)}</span>
              {r.user_rating_count ? (
                <span className="text-xs text-[#647466]">({r.user_rating_count})</span>
              ) : null}
            </div>
          )}
        </div>

        {r.address && (
          <div className="mt-2 flex items-start gap-1.5 text-sm text-[#647466]">
            <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#8A9A8B]" />
            <span className="line-clamp-2 leading-snug">{r.address}</span>
          </div>
        )}

        <div className="mt-3 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D95D39]">
            {(r.primary_type || "restaurant").replace(/_/g, " ")}
          </span>
          <HalalAttrChips r={r} />
          <div className="flex items-center gap-3">
            {onReport && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onReport();
                }}
                data-testid={`report-btn-${r.place_id}`}
                className="text-xs font-medium text-[#8A9A8B] hover:text-[#2A3B2C] transition-colors"
                title="Report incorrect or non-halal listing"
              >
                Looks off?
              </button>
            )}
            {onClaim && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClaim();
                }}
                data-testid={`claim-btn-${r.place_id}`}
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#2A3B2C] hover:text-[#D95D39] transition-colors"
                title="Own this restaurant? Verify it with proof."
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                Claim
              </button>
            )}
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`directions-link-${r.place_id}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#D95D39] hover:text-[#C54E2C] transition-colors"
            >
              Directions
              <Navigation className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
});

/**
 * HalalAttrChips — compact preparation badges shown on every card.
 * Renders nothing when owner hasn't set any flags, so we don't clutter
 * unverified listings.
 */
function HalalAttrChips({ r }) {
  const chips = [];
  if (r.halal_hand_slaughtered) chips.push({ id: "hand", label: "Hand-slaughtered" });
  if (r.halal_separate_cooking_area) chips.push({ id: "sep", label: "Separate kitchen" });
  if (r.halal_on_request) chips.push({ id: "req", label: "Halal on request" });
  if (chips.length === 0) return null;
  return (
    <div
      data-testid={`halal-attrs-${r.place_id}`}
      className="mt-2 mb-2 flex flex-wrap gap-1.5 w-full"
    >
      {chips.map((c) => (
        <span
          key={c.id}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#F0F8F4] text-[#1F5C42] border border-[#2E7D5B]/25"
        >
          <BadgeCheck className="w-2.5 h-2.5" />
          {c.label}
        </span>
      ))}
    </div>
  );
}

export default RestaurantCard;
